'use strict'

/**
 * 年次・四半期表示時に、月次 DataCreateTask の PeriodValue（例: 2027年6月度）を解決する。 決算月は Redux の accPeriod（例: 3月）を優先し、欠落時は PeriodValue
 * の「○年△月期」の月を使用する。 得た PeriodValue は loadMatrixMappingPair が「どの data_create_task_key で月次マッピングを取るか」に使う。
 */

import { PERIOD_MONTHLY, PERIOD_QUARTER, PERIOD_YEAR } from './constants'

/**
 * 暦の年月に delta ヶ月を加算する（1〜12 月、負の delta 可）
 *
 * @param {number} year
 * @param {number} month - 1〜12
 * @param {number} deltaMonths
 * @returns {{ year: number; month: number }}
 */
export function addCalendarMonths(year, month, deltaMonths) {
  const idx = year * 12 + (month - 1) + deltaMonths
  const ny = Math.floor(idx / 12)
  const nm = (idx % 12) + 1
  return { year: ny, month: nm }
}

/**
 * AccPeriod 文字列から決算月（1〜12）を取り出す
 *
 * @param {string | null | undefined} accPeriod - 例: "3月"
 * @returns {number | null}
 */
export function parseClosingMonthFromAccPeriod(accPeriod) {
  if (accPeriod == null || typeof accPeriod !== 'string') return null
  const m = accPeriod.match(/^(\d{1,2})月/)
  if (!m) return null
  const n = parseInt(m[1], 10)
  if (Number.isNaN(n) || n < 1 || n > 12) return null
  return n
}

/**
 * PeriodValue から決算期の終了西暦年と決算月を得る（年次・四半期のラベル用）
 *
 * @param {string} periodValue - 例: "2028年3月期", "2028年3月期1Q"
 * @param {string | null | undefined} accPeriod - 決算月（優先）
 * @returns {{ yEnd: number; fMonth: number } | null}
 */
export function parseFiscalYearEndYearAndMonth(periodValue, accPeriod) {
  if (!periodValue || typeof periodValue !== 'string') return null
  const ym = periodValue.match(/^(\d{4})年(\d{1,2})月期/)
  if (!ym) return null
  const yEnd = parseInt(ym[1], 10)
  const monthFromLabel = parseInt(ym[2], 10)
  const fromAcc = parseClosingMonthFromAccPeriod(accPeriod)
  const fMonth = fromAcc != null ? fromAcc : monthFromLabel >= 1 && monthFromLabel <= 12 ? monthFromLabel : null
  if (fMonth == null || Number.isNaN(yEnd)) return null
  return { yEnd, fMonth }
}

/**
 * 四半期ラベル末尾の Q 番号（1〜4）
 *
 * @param {string} periodValue
 * @returns {number | null}
 */
export function parseQuarterSuffix(periodValue) {
  if (!periodValue || typeof periodValue !== 'string') return null
  const m = periodValue.match(/([1-4])Q\s*$/i)
  if (!m) return null
  return parseInt(m[1], 10)
}

/**
 * 月次タスクの PeriodValue 形式（IF02028 例に合わせる）
 *
 * @param {number} year
 * @param {number} month - 1〜12
 * @returns {string}
 */
export function formatMonthlyPeriodValueJapanese(year, month) {
  return `${year}年${month}月度`
}

/**
 * 選択中の年次・四半期に対応する「参照用月次」の PeriodValue を返す
 *
 * @param {string} periodKind - Year | quarter | monthly
 * @param {string} periodValue - DataCreateTask の PeriodValue
 * @param {string | null | undefined} accPeriod
 * @returns {string | null}
 */
export function resolveMonthlyPeriodValueForMatrixSelection(periodKind, periodValue, accPeriod) {
  if (periodKind === PERIOD_MONTHLY) return null
  const parsed = parseFiscalYearEndYearAndMonth(periodValue, accPeriod)
  if (!parsed) return null
  const { yEnd, fMonth } = parsed
  const fyFirst = addCalendarMonths(yEnd, fMonth, -11)
  if (periodKind === PERIOD_YEAR) {
    return formatMonthlyPeriodValueJapanese(yEnd, fMonth)
  }
  if (periodKind === PERIOD_QUARTER) {
    const q = parseQuarterSuffix(periodValue)
    if (q == null) return null
    const last = addCalendarMonths(fyFirst.year, fyFirst.month, 3 * q - 1)
    return formatMonthlyPeriodValueJapanese(last.year, last.month)
  }
  return null
}

/**
 * 月次 PeriodValue に一致する DataCreateTask の Key を検索
 *
 * @param {unknown[]} taskListFull
 * @param {string | null} periodValueJapanese
 * @returns {number | null}
 */
export function findMonthlyDataCreateTaskKey(taskListFull, periodValueJapanese) {
  if (!periodValueJapanese || !Array.isArray(taskListFull)) return null
  for (const t of taskListFull) {
    const pk = t.periodKind ?? t.PeriodKind
    const pv = t.periodValue ?? t.PeriodValue
    if (pk === 'monthly' && pv === periodValueJapanese) {
      const k = t.key ?? t.Key
      if (k == null) continue
      return typeof k === 'number' ? k : parseInt(String(k), 10)
    }
  }
  return null
}

/**
 * 年次・四半期の詳細期間に紐づく「月次マッピング参照用」の DataCreateTask Key
 *
 * @param {unknown[]} taskListFull
 * @param {unknown | null} selectedTask - 詳細期間で選択中の 1 件
 * @param {string} periodKind
 * @param {string | null | undefined} accPeriod
 * @returns {number | null}
 */
export function resolveMonthlyTaskKeyForMatrixSelection(taskListFull, selectedTask, periodKind, accPeriod) {
  if (!selectedTask || periodKind === PERIOD_MONTHLY) return null
  const pv = selectedTask.periodValue ?? selectedTask.PeriodValue
  if (!pv || typeof pv !== 'string') return null
  const monthlyPv = resolveMonthlyPeriodValueForMatrixSelection(periodKind, pv, accPeriod)
  if (!monthlyPv) return null
  return findMonthlyDataCreateTaskKey(taskListFull, monthlyPv)
}
